import GenericScreenLayout from 'src/layouts/GenericScreenLayout';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGetTransactions } from 'src/hooks/transactions';
import { ITransactionsData } from '@interfaces/transactions';
import { filterProp } from '@interfaces/transactionsFilters';
import merchantImage from 'src/global/assets/images/stores-merchant.png';
import EmptyList from 'src/components/EmptyList';
import PaginationComponent from 'src/components/PaginationComponent';
import { transactionPaginationEvent } from 'src/analitycs/pagesEvents/transactions';
import transactionsServices from 'src/services/transactions/axios/transactionsServices';
import { getUserCuits } from 'src/redux/reducers/user';
import { useSelector } from 'react-redux';
import ErrorList from 'src/components/ErrorList';
import errorIcon from 'src/global/assets/icons/not-found.svg';

import { parseFiltersFromParams, serializeFiltersToParams, filtersAreEqual, hasAnyFilterValue } from './utils/urlFilters';
import { withDefaultRange } from './utils';
import DescargaPanel from './DescargaPanel';
import MetricsSummary from './MetricsSummary';
import TableListTransactions from './TableListTransactions';

const LIMIT = 20;
const EMPTY_MESSAGE = 'No hay pagos registrados para la búsqueda seleccionada';

/**
 * Vista de la tab Descarga (H4/H5/H6): panel de filtros con aplicación explícita,
 * KPIs y tabla definitiva. La URL es la única fuente de verdad de los filtros
 * aplicados (rehidrata al montar; se escribe al Aplicar).
 */
const TransactionsComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const userCuits: string[] = useSelector(getUserCuits);

  const [filter, setFilter] = useState<filterProp>(() => parseFiltersFromParams(searchParams));
  const [logInAmplitude, setLogInAmplitude] = useState(false);
  const [requestPagination, setRequestPagination] = useState<{ page: number; limit: number }>({
    page: 0,
    limit: LIMIT
  });

  const isFirstFilterSync = useRef(true);

  // filter → URL (preservando el query param de la tab). Primer render ya viene de la URL.
  useEffect(() => {
    if (isFirstFilterSync.current) {
      isFirstFilterSync.current = false;
      return;
    }
    const next = serializeFiltersToParams(filter);
    const tab = searchParams.get('tab');
    if (tab) next.set('tab', tab);
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  // H6: sin filtros aplicados, el listado refleja los ultimos 30 dias — el mismo
  // rango que la barra de KPIs, para que ambos describan el mismo universo.
  const effectiveFilter = useMemo(
    () => withDefaultRange(filter, hasAnyFilterValue(filter)),
    [filter]
  );

  const serviceParams = {
    filterUrlParams: effectiveFilter,
    pagination: requestPagination
  };

  const cleanLogData = () => {
    setLogInAmplitude(false);
  };

  const {
    data: transactionsResult,
    isFetching: isFetchingTransactions,
    error: errorTransactions
  } = useGetTransactions({ serviceParams, logInAmplitude, cleanLogData });
  const transactions: ITransactionsData[] = transactionsResult?.data?.transactions;

  const handlePagination = (selectPage: number) => {
    window.scrollTo({ top: 0, left: 0 });
    transactionPaginationEvent(selectPage + 1, transactionsResult?.metadata?.pagination.total_pages);
    setRequestPagination({ page: selectPage, limit: LIMIT });
  };

  // Única vía de aplicación de filtros (H4: on-submit del panel).
  const handleApply = (newFilter: filterProp) => {
    if (filtersAreEqual(filter, newFilter)) return;
    setRequestPagination({ page: 0, limit: LIMIT });
    setLogInAmplitude(true);
    setFilter(newFilter);
  };

  // Exporta con el mismo rango que muestra la tabla: con `filter` crudo, un CSV
  // sin filtros aplicados salia sin fechas y no coincidia con lo que se veia.
  const onHandleDownloadCSV = async () => {
    const transactionsToExport = await transactionsServices.exportTransactions(effectiveFilter);
    return transactionsToExport;
  };

  const hasValues = transactions?.length > 0 && !isFetchingTransactions && !errorTransactions;

  return (
    <GenericScreenLayout isLoading={isFetchingTransactions}>
      <GenericScreenLayout.Header
        title={'Mis Pagos'}
        titleVariant="Title-Medium"
        subTitle={userCuits?.length && userCuits?.length < 2 ? `CUIT: ${userCuits[0]}` : ''}
      />

      <GenericScreenLayout.SearchAndFilterWithChildren
        component={
          <>
            <DescargaPanel
              appliedFilter={filter}
              onApply={handleApply}
              onDownloadCSV={onHandleDownloadCSV}
              downloadFileName={`Transacciones ${userCuits?.length === 1 ? userCuits[0] : ''}.csv`}
              isLoading={isFetchingTransactions}
            />
            <MetricsSummary
              filter={filter}
              totalFromList={transactionsResult?.metadata?.pagination?.total_results}
            />
          </>
        }
      />
      {errorTransactions && (
        <GenericScreenLayout.MainContent
          mainContent={
            <ErrorList
              title={'No pudimos cargar esta página.'}
              subtitle={errorTransactions?.message}
              image={errorIcon}
            />
          }
        />
      )}
      {transactions?.length === 0 && !isFetchingTransactions && !errorTransactions && (
        <GenericScreenLayout.MainContent
          mainContent={<EmptyList image={merchantImage} message={EMPTY_MESSAGE} hasButton={false} />}
        />
      )}
      {hasValues && (
        <GenericScreenLayout.MainContent mainContent={<TableListTransactions transactions={transactions} />} />
      )}
      {hasValues && (
        <GenericScreenLayout.Pagination
          component={
            <PaginationComponent
              handlePagination={handlePagination}
              paginationNumberStart={1}
              paginationResult={{
                page: transactionsResult?.metadata?.pagination.page,
                totalPages: transactionsResult?.metadata?.pagination.total_pages
              }}
            />
          }
        />
      )}
    </GenericScreenLayout>
  );
};

export default TransactionsComponent;
